import { useState } from 'react';
import { useI18n } from './i18n';
import { deleteAccount, isError, type DeletionReport } from './api';

/**
 * Leaving, and taking everything.
 *
 * Its own component rather than another panel inside `Objectives`, for the reason
 * that screen already gives about shared state three times over: every action there
 * keeps its own in-flight flag and its own message because one piece of error state
 * read in two panels is the same complaint pinned to unrelated controls — which
 * reached Leo as "the error came up in multiple places". This is the action with the
 * least business borrowing anybody's: a failure here means data is half gone.
 *
 * **Nothing about this panel is designed to be easy.** Three deliberate steps — read
 * it, tick it, confirm it — and none of them is a dark pattern in reverse: the
 * button is plainly available and plainly labelled, it is simply not pressable by
 * accident. A learner who means it gets through in four seconds; a thumb on a phone
 * does not get through at all.
 *
 * The confirmation dialog is the same tool `start` and `reactivate` use, and here it
 * is the last thing between somebody and a curriculum they spent weeks answering.
 */
export default function DeleteAccount() {
  const { t } = useI18n();
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  /*
   * What was destroyed, kept so the last thing the learner sees is a count rather
   * than a reassurance. "Your data has been deleted" is what a screen says whether
   * or not it happened; "476 things you had remembered" is what a screen says when
   * it did.
   */
  const [done, setDone] = useState<DeletionReport | undefined>();

  function explain(error: string): string {
    if (error === 'not_found') return t('del.error.notFound');
    if (error === 'not_confirmed') return t('del.error.notConfirmed');
    if (error === 'sign_in_required') return t('del.error.notFound');
    return t('del.failed');
  }

  async function erase() {
    if (!acknowledged) return;
    if (!window.confirm(t('del.confirm'))) return;

    setBusy(true);
    setProblem(undefined);

    const result = await deleteAccount();
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    setDone(result.deleted);
  }

  if (done) {
    const counts = t('del.done.counts').replace(
      /\{(\w+)\}/g,
      (_, name: string) => String((done as unknown as Record<string, number>)[name] ?? 0),
    );
    return (
      <section className="panel">
        <h2>{t('del.done.title')}</h2>
        <p className="muted" role="status">
          {t('del.done.body')}
        </p>
        <p className="muted">{counts}</p>
        <div className="actions">
          {/*
           * A full page load rather than a route change. The session cookie was
           * cleared by the response that did the deletion, and every piece of state
           * still in this tab — the objective, the curriculum, the progress panel —
           * belongs to a learner who no longer exists. Reloading is how the app
           * stops holding a dead account in memory.
           */}
          <button
            type="button"
            className="button primary"
            onClick={() => window.location.assign('/')}
          >
            {t('del.done.home')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>{t('del.title')}</h2>
      <p className="muted">{t('del.body')}</p>
      {/* The sentence that must not be softened, on its own line so it is not
          skimmed past inside a paragraph about features. */}
      <p className="problem detail">{t('del.irreversible')}</p>
      {/* And the exception, named. "Everything" with something quietly kept back is
          exactly the claim a privacy right gets exercised against. */}
      <p className="muted small">{t('del.kept')}</p>

      <label className="choice">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          disabled={busy}
        />
        <span>{t('del.ack')}</span>
      </label>

      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}

      <div className="actions">
        <button
          type="button"
          className="button"
          onClick={() => void erase()}
          disabled={!acknowledged || busy}
        >
          {busy ? t('del.working') : t('del.button')}
        </button>
      </div>
    </section>
  );
}
